/**
 * WerkstattAuftragFotoUpload — Foto-Upload für Werkstatt-Aufträge
 *
 * Zwei feste Kategorien:
 *   • schadensdoku        — Ausgangszustand vor Reparatur
 *   • reparaturfortschritt — Während/nach Reparatur
 *
 * Pattern bewusst analog zu `AktenOrdner.tsx`:
 *   • Bucket: `akten-dateien` (Reuse)
 *   • Pfad:   werkstatt-auftraege/{auftragsId}/{kategorie}/{ts}_{filename}
 *   • Metadaten kommen direkt aus Supabase Storage (`metadata.size`,
 *     `metadata.mimetype`, `created_at`) — keine separate Tabelle.
 *
 * Mobile-first: File-Input mit `accept="image/*" capture="environment"`,
 * sodass Mechaniker direkt aus der Kamera fotografieren können.
 *
 * Issue #394.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Trash2, Upload, Wrench, X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

// ─── Konstanten ───────────────────────────────────────────────────────────────

const BUCKET = 'akten-dateien';
const MAX_DATEI_GROESSE = 25 * 1024 * 1024; // 25 MB analog Akten-Upload
const ERLAUBTE_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

export type FotoKategorie = 'schadensdoku' | 'reparaturfortschritt';

interface KategorieDef {
  id: FotoKategorie;
  titel: string;
  beschreibung: string;
  icon: React.ReactNode;
  farbe: string;
}

const KATEGORIEN: KategorieDef[] = [
  {
    id: 'schadensdoku',
    titel: 'Schadensdokumentation',
    beschreibung: 'Ausgangszustand vor Reparatur',
    icon: <Camera className="w-5 h-5" />,
    farbe: '#B91C1C',
  },
  {
    id: 'reparaturfortschritt',
    titel: 'Reparaturfortschritt',
    beschreibung: 'Während und nach der Reparatur',
    icon: <Wrench className="w-5 h-5" />,
    farbe: '#0D9488',
  },
];

// ─── Typen ────────────────────────────────────────────────────────────────────

interface AuftragFoto {
  id: string;
  name: string;
  path: string;
  url: string;
  groesse: number;
  mime_type: string;
  hochgeladen_von: string | null;
  erstellt_am: string;
}

interface Props {
  auftragsId: string;
  /** Optional: User-ID des Mechanikers, wird in Storage-Metadaten gespeichert */
  userId?: string | null;
  /** Optional: Ist der aktuelle Nutzer Admin? Erlaubt Löschen fremder Bilder */
  istAdmin?: boolean;
}

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function buildPath(auftragsId: string, kategorie: FotoKategorie): string {
  return `werkstatt-auftraege/${auftragsId}/${kategorie}`;
}

function sanitizeName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

function formatGroesse(bytes: number): string {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Galerie pro Kategorie ────────────────────────────────────────────────────

function FotoKategorieBlock({
  auftragsId,
  userId,
  istAdmin,
  kategorie,
}: Props & { kategorie: KategorieDef }) {
  const [fotos, setFotos] = useState<AuftragFoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState<AuftragFoto | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const storagePath = buildPath(auftragsId, kategorie.id);

  const loadFotos = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .list(storagePath, { limit: 200, sortBy: { column: 'created_at', order: 'desc' } });
      if (error) {
        console.warn(`[WerkstattFoto] list ${storagePath}:`, error.message);
        setFotos([]);
        return;
      }
      const items: AuftragFoto[] = (data || [])
        .filter((f) => f.name !== '.emptyFolderPlaceholder')
        .map((f) => {
          const fullPath = `${storagePath}/${f.name}`;
          const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(fullPath);
          return {
            id: f.id || f.name,
            name: f.name,
            path: fullPath,
            url: urlData.publicUrl,
            groesse: f.metadata?.size || 0,
            mime_type: f.metadata?.mimetype || 'image/*',
            hochgeladen_von: (f.metadata as { uploader?: string } | undefined)?.uploader ?? null,
            erstellt_am: f.created_at || new Date().toISOString(),
          };
        });
      setFotos(items);
    } finally {
      setLoading(false);
    }
  }, [storagePath]);

  useEffect(() => {
    loadFotos();
  }, [loadFotos]);

  const handleUpload = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;

      setUploading(true);
      let success = 0;
      for (const file of list) {
        if (!ERLAUBTE_MIME.includes(file.type)) {
          toast.error(`Nur Bilder erlaubt: ${file.name}`);
          continue;
        }
        if (file.size > MAX_DATEI_GROESSE) {
          toast.error(`Datei zu groß (max. 25 MB): ${file.name}`);
          continue;
        }
        const fileName = `${Date.now()}_${sanitizeName(file.name)}`;
        const { error } = await supabase.storage
          .from(BUCKET)
          .upload(`${storagePath}/${fileName}`, file, {
            cacheControl: '3600',
            upsert: false,
            contentType: file.type,
            metadata: userId ? { uploader: userId } : undefined,
          });
        if (error) {
          toast.error(`Upload fehlgeschlagen: ${file.name}`, { description: error.message });
        } else {
          success++;
        }
      }
      if (success > 0) {
        toast.success(`${success} Foto${success === 1 ? '' : 's'} hochgeladen`);
        await loadFotos();
      }
      setUploading(false);
    },
    [storagePath, loadFotos],
  );

  const handleDelete = useCallback(
    async (foto: AuftragFoto) => {
      if (!istAdmin && foto.hochgeladen_von && foto.hochgeladen_von !== userId) {
        toast.error('Sie können nur eigene Fotos löschen');
        return;
      }
      const { error } = await supabase.storage.from(BUCKET).remove([foto.path]);
      if (error) {
        toast.error('Fehler beim Löschen', { description: error.message });
        return;
      }
      toast.success('Foto gelöscht');
      setFotos((prev) => prev.filter((f) => f.id !== foto.id));
      if (lightbox?.id === foto.id) setLightbox(null);
    },
    [istAdmin, userId, lightbox],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) handleUpload(e.dataTransfer.files);
    },
    [handleUpload],
  );

  return (
    <section
      data-testid={`werkstatt-foto-upload-${kategorie.id}`}
      className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5"
    >
      <header className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
          style={{ backgroundColor: kategorie.farbe }}
        >
          {kategorie.icon}
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-900">{kategorie.titel}</h3>
          <p className="text-xs text-gray-500">{kategorie.beschreibung}</p>
        </div>
        <span className="text-xs text-gray-500" data-testid={`werkstatt-foto-count-${kategorie.id}`}>
          {fotos.length} Foto{fotos.length === 1 ? '' : 's'}
        </span>
      </header>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors mb-4 ${
          dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50'
        }`}
      >
        <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600 mb-3">
          Bilder hierher ziehen oder per Knopf hochladen
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          data-testid={`werkstatt-foto-input-${kategorie.id}`}
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              handleUpload(e.target.files);
              e.target.value = '';
            }
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
          style={{ backgroundColor: kategorie.farbe }}
          data-testid={`werkstatt-foto-button-${kategorie.id}`}
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Camera className="w-4 h-4" />
          )}
          {uploading ? 'Lade hoch…' : 'Fotos hochladen'}
        </button>
      </div>

      <div data-testid={`werkstatt-foto-galerie-${kategorie.id}`}>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : fotos.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Noch keine Fotos</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {fotos.map((foto) => {
              const darfLoeschen =
                istAdmin || !foto.hochgeladen_von || foto.hochgeladen_von === userId;
              return (
                <div
                  key={foto.id}
                  className="group relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50 aspect-square"
                  data-testid={`werkstatt-foto-item-${kategorie.id}`}
                >
                  <button
                    type="button"
                    onClick={() => setLightbox(foto)}
                    className="absolute inset-0 w-full h-full"
                    aria-label={`Foto ansehen: ${foto.name}`}
                  >
                    <img
                      src={foto.url}
                      alt={foto.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </button>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-[10px] text-white truncate">{formatGroesse(foto.groesse)}</p>
                  </div>
                  {darfLoeschen && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(foto);
                      }}
                      className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white/90 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      aria-label="Foto löschen"
                      data-testid={`werkstatt-foto-delete-${kategorie.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid={`werkstatt-foto-lightbox-${kategorie.id}`}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white p-2"
            onClick={() => setLightbox(null)}
            aria-label="Schließen"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightbox.url}
            alt={lightbox.name}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

export default function WerkstattAuftragFotoUpload({ auftragsId, userId, istAdmin }: Props) {
  return (
    <div className="space-y-4">
      {KATEGORIEN.map((kat) => (
        <FotoKategorieBlock
          key={kat.id}
          auftragsId={auftragsId}
          userId={userId}
          istAdmin={istAdmin}
          kategorie={kat}
        />
      ))}
    </div>
  );
}
